import { watch } from "vue";
import type { NotationAttributes } from "common/baseTypes";
import type {
  PracticeShareCoach,
  PracticeShareOp,
  PracticeShareRecord,
  PracticeShareSnapshot,
} from "common/practiceSessionStore";
import { FeathersHelper } from "./feathersHelper";
import useApiHelper from "./apiHelper";
import { GUEST_BOARD_USER } from "./boardUserHelper";
import { useNotationStore } from "../store/pinia/notationStore";
import { usePracticeStore, type PracticeSession } from "../store/pinia/practiceStore";

const PARTICIPANT_KEY = "mathboard-practice-participant";
const HOST_SESSION_KEY = "mathboard-practice-share-host";

type ShareState = {
  sessionId: string;
  hostParticipantId: string;
  participantId: string;
  questionUUId: string;
};

const state: ShareState = {
  sessionId: "",
  hostParticipantId: "",
  participantId: "",
  questionUUId: "",
};

let applyingRemote = false;
let listening = false;
let sessionWatchStarted = false;
let coachHandler: ((coach: PracticeShareCoach | null, fromParticipantId: string) => void) | null =
  null;

export function getPracticeParticipantId(): string {
  let id = sessionStorage.getItem(PARTICIPANT_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(PARTICIPANT_KEY, id);
  }
  return id;
}

export function practiceShareSessionId(): string {
  return state.sessionId;
}

export function isPracticeShareActive(): boolean {
  return state.sessionId.length > 0;
}

export function isPracticeShareHost(): boolean {
  return (
    isPracticeShareActive() &&
    state.participantId === state.hostParticipantId
  );
}

export function isApplyingRemotePracticeShare(): boolean {
  return applyingRemote;
}

export function setPracticeShareCoachHandler(
  handler: ((coach: PracticeShareCoach | null, fromParticipantId: string) => void) | null,
) {
  coachHandler = handler;
}

function remember(row: PracticeShareRecord) {
  state.sessionId = row.id;
  state.hostParticipantId = row.hostParticipantId;
  state.participantId = getPracticeParticipantId();
  state.questionUUId = row.snapshot.questionUUId;
}

export function stampedPracticeNotation(
  notation: NotationAttributes,
): NotationAttributes {
  const participantId = getPracticeParticipantId();
  const user = notation.user;
  if (user?.uuid && user.uuid !== GUEST_BOARD_USER.uuid) return notation;
  return {
    ...notation,
    user: { ...GUEST_BOARD_USER, ...user, uuid: participantId },
  };
}

async function joinChannel() {
  await FeathersHelper.waitUntilConnected();
  await FeathersHelper.getInstance()
    .service("practiceSessionJoin")
    .create({
      sessionId: state.sessionId,
      participantId: state.participantId,
    });
}

export async function createPracticeShare(
  snapshot: PracticeShareSnapshot,
): Promise<PracticeShareRecord> {
  const hostParticipantId = getPracticeParticipantId();
  const row = await useApiHelper().createPracticeShareSession({
    hostParticipantId,
    snapshot,
  });
  remember(row);
  sessionStorage.setItem(HOST_SESSION_KEY, row.id);
  await joinChannel();
  return row;
}

/** Host refresh stays on the original practice page and rejoins the channel. */
export async function rejoinHostShare(questionUUId: string): Promise<boolean> {
  if (isPracticeShareActive()) {
    if (state.questionUUId === questionUUId) return true;
    state.sessionId = "";
    state.questionUUId = "";
    return false;
  }
  const sessionId = sessionStorage.getItem(HOST_SESSION_KEY);
  if (!sessionId) return false;
  try {
    const row = await useApiHelper().getPracticeShareSession(sessionId);
    if (
      row.snapshot.questionUUId !== questionUUId ||
      row.hostParticipantId !== getPracticeParticipantId()
    ) {
      return false;
    }
    remember(row);
    await joinChannel();
    return true;
  } catch {
    sessionStorage.removeItem(HOST_SESSION_KEY);
    return false;
  }
}

export function installPracticeShareSnapshot(row: PracticeShareRecord) {
  const practiceStore = usePracticeStore();
  const questionUUId = row.snapshot.questionUUId;
  applyingRemote = true;
  try {
    practiceStore.setNotations(
      questionUUId,
      row.snapshot.notations.map((notation) => ({
        ...notation,
        boardType: "PRACTICE",
        parentUUId: questionUUId,
      })),
    );
    practiceStore.setSession(questionUUId, {
      ...practiceStore.getSession(questionUUId),
      ...row.snapshot.session,
    } as PracticeSession);
  } finally {
    applyingRemote = false;
  }
}

export async function openPracticeShare(
  sessionId: string,
): Promise<PracticeShareRecord> {
  const row = await useApiHelper().claimPracticeShareSeat(
    sessionId,
    getPracticeParticipantId(),
  );
  remember(row);
  await joinChannel();
  return row;
}

async function publish(op: PracticeShareOp) {
  if (!isPracticeShareActive() || applyingRemote) return;
  try {
    await FeathersHelper.waitUntilConnected();
    await FeathersHelper.getInstance()
      .service("practiceSessionSync")
      .create({ ...op, sessionId: state.sessionId });
  } catch (error) {
    console.error(error);
  }
}

export function publishPracticeShareNotation(
  op: "create" | "update" | "remove",
  notationOrId: NotationAttributes | string,
) {
  if (!isPracticeShareActive() || applyingRemote) return;
  if (op === "remove") {
    void publish({
      op: "remove",
      participantId: getPracticeParticipantId(),
      notationUUId: String(notationOrId),
    });
    return;
  }
  void publish({
    op,
    participantId: getPracticeParticipantId(),
    notation: stampedPracticeNotation(notationOrId as NotationAttributes),
  });
}

export function publishPracticeShareCoach(coach: PracticeShareCoach | null) {
  void publish({
    op: "coach",
    participantId: getPracticeParticipantId(),
    coach,
  });
}

function publishSession(session: PracticeSession) {
  void publish({
    op: "session",
    participantId: getPracticeParticipantId(),
    session: { ...session },
  });
}

export function watchPracticeShareSession() {
  if (sessionWatchStarted) return;
  sessionWatchStarted = true;
  const practiceStore = usePracticeStore();
  const notationStore = useNotationStore();
  watch(
    () => {
      const questionUUId = notationStore.getParent()?.uuid;
      if (!questionUUId || notationStore.getParent()?.type !== "PRACTICE") {
        return "";
      }
      return JSON.stringify(practiceStore.getSession(questionUUId));
    },
    (json, prev) => {
      if (!json || json === prev || applyingRemote || !isPracticeShareActive()) {
        return;
      }
      publishSession(JSON.parse(json) as PracticeSession);
    },
  );
}

function applyRemote(op: PracticeShareOp & { sessionId?: string }) {
  if (!op?.sessionId || op.sessionId !== state.sessionId) return;
  if (op.participantId === getPracticeParticipantId()) return;
  const notationStore = useNotationStore();
  const practiceStore = usePracticeStore();
  const questionUUId = notationStore.getParent()?.uuid;
  if (!questionUUId) return;

  applyingRemote = true;
  try {
    if (op.op === "create" || op.op === "update") {
      const notation = {
        ...op.notation,
        boardType: "PRACTICE" as const,
        parentUUId: questionUUId,
      };
      practiceStore.upsertNotation(questionUUId, notation);
      notationStore.addNotation(notation, true, false);
      return;
    }
    if (op.op === "remove") {
      practiceStore.removeNotation(questionUUId, op.notationUUId);
      if (notationStore.getNotation(op.notationUUId)) {
        notationStore.deleteNotation(op.notationUUId);
      }
      return;
    }
    if (op.op === "session") {
      practiceStore.setSession(questionUUId, {
        ...practiceStore.getSession(questionUUId),
        ...op.session,
      } as PracticeSession);
      return;
    }
    if (op.op === "coach") {
      coachHandler?.(op.coach, op.participantId);
    }
  } finally {
    applyingRemote = false;
  }
}

export function syncIncomingPracticeShare() {
  if (listening) return;
  listening = true;
  const feathersClient = FeathersHelper.getInstance();
  feathersClient
    .service("practiceSessionSync")
    .on("created", (op: PracticeShareOp & { sessionId?: string }) => {
      applyRemote(op);
    });
  watchPracticeShareSession();
}
